import { useEffect, useRef } from 'react';

// WebOTP isn't in TS's DOM lib yet (Chrome-on-Android only; no-op elsewhere).
// See https://developer.mozilla.org/en-US/docs/Web/API/WebOTP_API
interface OTPCredential extends Credential {
  code: string;
}

// Backend SMS must end with a line like "@your.domain.com #123456" for Chrome to associate
// the code with this page. See docs/webotp.md (or equivalent) for the exact message format.
const useWebOtpAutofill = (enabled: boolean, onCode: (code: string) => void) => {
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  useEffect(() => {
    if (!enabled || !('OTPCredential' in window) || !navigator.credentials?.get) return;

    const controller = new AbortController();

    (
      navigator.credentials.get({
        // @ts-expect-error -- `otp` isn't in TS's CredentialRequestOptions yet
        otp: { transport: ['sms'] },
        signal: controller.signal,
      }) as Promise<OTPCredential>
    ).then(
      otp => {
        if (otp?.code) onCodeRef.current(otp.code);
      },
      () => {
        // Ignored: aborted on cleanup, user declined, or no SMS received. Using the two-arg
        // form of `then` (not a chained `.catch`) so a bug in onCodeRef.current isn't also
        // swallowed here - that would surface as an unhandled rejection instead.
      },
    );

    return () => controller.abort();
  }, [enabled]);
};

export default useWebOtpAutofill;
